import { Box, Typography, IconButton, Chip } from '@mui/material';
import { DASH_PLACEHOLDER } from './missingValue';

interface Material {
    id: string;
    seedWeight: string;
    seedWeightUnit?: string;
    seedCount: number;
    priority: 'High' | 'Medium' | 'Low' | 'NA';
}

interface MaterialDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    materials?: Material[];
    spLotId?: string;
    mode?: 'materials' | 'splots';
    spLotIds?: string[];
}

export const MaterialDetailsDialog = ({
    open,
    onClose,
    materials = [],
    mode = 'materials',
    spLotIds = []
}: MaterialDetailsDialogProps) => {
    if (!open) return null;

    const isSPLotsMode = mode === 'splots';
    const displayItems = isSPLotsMode ? spLotIds : materials;
    const count = displayItems.length;

    return (
        <Box sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#fff'
            }}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: '#000',
                        p: 0.5
                    }}
                    aria-label="back"
                >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.5 21L10.5 14L17.5 7" stroke="#23272F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </IconButton>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.4625rem' }}>
                        {isSPLotsMode ? `SP Lots (${count})` : `Material IDs (${count})`}
                    </Typography>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: '#fafafa' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSPLotsMode ? 0 : 2 }}>
                    {isSPLotsMode ? (
                        // SP Lots List
                        spLotIds.map((spLotId, index) => (
                            <Box
                                key={`${spLotId}-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 2,
                                    borderBottom: index < spLotIds.length - 1 ? '1px solid #e0e0e0' : 'none'
                                }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.21875rem' }}>
                                    {index + 1}. {spLotId}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        // Materials List
                        materials.map((material, index) => (
                            (() => {
                                const hasWeight = material.seedWeight !== undefined
                                    && material.seedWeight !== null
                                    && String(material.seedWeight).trim() !== ''
                                    && String(material.seedWeight).trim() !== DASH_PLACEHOLDER;
                                const weightText = hasWeight
                                    ? `${material.seedWeight} ${material.seedWeightUnit || 'g'}`
                                    : DASH_PLACEHOLDER;
                                return (
                            <Box
                                key={`${material.id}-${index}`}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    pb: 2,
                                    borderBottom: index < materials.length - 1 ? '1px solid #e0e0e0' : 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.21875rem', minWidth: '20px' }}>
                                        {index + 1}.
                                    </Typography>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.21875rem', mb: 0.5 }}>
                                            {material.id}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '1.05625rem' }}>
                                            Weight: {weightText} Count: {material.seedCount}
                                        </Typography>
                                    </Box>
                                </Box>
                                {material.priority && (
                                    <Chip
                                        label={material.priority}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.975rem',
                                            height: '24px',
                                            borderRadius: '14px',
                                            backgroundColor:
                                                material.priority === 'High' ? '#F9A2A6' :
                                                material.priority === 'Medium' ? '#F7E392' :
                                                material.priority === 'Low' ? '#85D895' :
                                                material.priority === 'NA' ? '' :
                                                '',
                                            color:'black',
                                            border: 'none',
                                            flexShrink: 0,
                                            px: 1.5,
                                            '& .MuiChip-label': {
                                                px: 0,
                                                whiteSpace: 'nowrap'
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                                );
                            })()
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
};
